/* ==========================================================================
   ChewCheer — interactions
   Vanilla JS, no dependencies. Every block is defensive: if a section's
   markup isn't present the block simply does nothing, so this file is safe
   to load on any subset of the page.
   ========================================================================== */
(() => {
  'use strict';

  /* ---------------------------------------------------------------- Shopify */
  /* The site is static; checkout is handed to the Shopify store. A cart
     permalink of the form /cart/<variantId>:<qty> drops straight into
     Shopify's hosted checkout with the item already in the cart. */
  /* The variant is read off the product section rather than hardcoded, because
     the /pages/a2 price test renders the same design against a different
     Shopify product. Whatever price that page displays, it must add the variant
     that actually costs that much, or the customer is charged something other
     than what they clicked. The constant is only a fallback. */
  const SHOPIFY = {
    domain: 'chewcheer.in',
    get variantId() {
      const el = document.querySelector('[data-variant-id]');
      if (!el) return '44613167513703';        /* old markup: original variant */
      /* The attribute exists but is empty, which means a price-test page was
         published without its product wired up. Return nothing rather than
         falling back, so buying is blocked instead of charging a price the
         visitor never saw. */
      return (el.dataset.variantId || '').trim();
    },
  };
  /* Any CTA can still carry a discount by adding data-discount="CODE"; the
     header's "Subscribe & Save 10%" button that used it has been removed. */

  const cartUrl = (qty = 1, discount) => {
    const n = Math.max(1, parseInt(qty, 10) || 1);
    const base = `https://${SHOPIFY.domain}/cart/${SHOPIFY.variantId}:${n}`;
    return discount ? `${base}?discount=${encodeURIComponent(discount)}` : base;
  };

  const $  = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  /* ------------------------------------------------------- quantity stepper */
  /* Drives the pack stepper and keeps every Pre-Order link in sync. */
  const qtyState = { value: 1 };

  function syncPreorderLinks() {
    const id = SHOPIFY.variantId;
    $$('[data-preorder]').forEach((el) => {
      if (!id) {
        el.setAttribute('aria-disabled', 'true');
        el.classList.add('is-unconfigured');
        el.removeAttribute('href');
        return;
      }
      el.href = cartUrl(qtyState.value, el.dataset.discount || undefined);
    });
    if (!id) console.warn('[ChewCheer] No variant id on this template. Buying is disabled until one is set.');
  }

  function initStepper() {
    const dec = $('[data-qty-dec]');
    const inc = $('[data-qty-inc]');
    const out = $('[data-qty-value]');
    if (!out) return syncPreorderLinks();

    const label = out.dataset.qtyLabel || 'Pack of {n}';
    const perPack = parseInt(out.dataset.qtyPerPack, 10) || 0;
    const unitOut = $('[data-qty-units]');

    const render = () => {
      out.textContent = label.replace('{n}', qtyState.value);
      if (unitOut && perPack) {
        const total = perPack * qtyState.value;
        unitOut.textContent = `${total} chews`;
      }
      if (dec) dec.disabled = qtyState.value <= 1;
      syncPreorderLinks();
    };

    dec && dec.addEventListener('click', (e) => {
      e.preventDefault();
      if (qtyState.value > 1) { qtyState.value -= 1; render(); }
    });
    inc && inc.addEventListener('click', (e) => {
      e.preventDefault();
      if (qtyState.value < 20) { qtyState.value += 1; render(); }
    });
    render();
  }

  /* --------------------------------------------------------- product gallery */
  function initGallery() {
    const main = $('[data-gallery-main]');
    const thumbs = $$('[data-gallery-thumb]');
    if (!main || !thumbs.length) return;

    let index = 0;

    const select = (thumb) => {
      index = thumbs.indexOf(thumb);
      const src = thumb.dataset.src || (thumb.querySelector('img') || {}).src;
      if (src) main.src = src;
      const alt = thumb.dataset.alt || (thumb.querySelector('img') || {}).alt;
      if (alt) main.alt = alt;
      thumbs.forEach((t) => {
        const on = t === thumb;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', String(on));
      });
    };

    thumbs.forEach((t) => {
      t.addEventListener('click', (e) => { e.preventDefault(); select(t); });
      t.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select(t); }
      });
    });

    /* The prev/next chevrons were in the markup but had no handler — clicking
       them did nothing. They step through the same thumb list, wrapping around. */
    const step = (delta) => select(thumbs[(index + delta + thumbs.length) % thumbs.length]);
    const prev = $('.s02__nav--prev');
    const next = $('.s02__nav--next');
    prev && prev.addEventListener('click', (e) => { e.preventDefault(); step(-1); });
    next && next.addEventListener('click', (e) => { e.preventDefault(); step(1); });

    select(thumbs.find((t) => t.classList.contains('is-active')) || thumbs[0]);
  }

  /* --------------------------------------------------- floating pre-order CTA */
  /* Appears once the hero is scrolled past, and gets out of the way while the
     product block itself is on screen (where the real buy controls live). */
  function initFloatingCta() {
    const cta = $('[data-float-cta]');
    if (!cta) return;
    const product = $('#product');

    let productVisible = false;
    if (product && 'IntersectionObserver' in window) {
      new IntersectionObserver(
        ([entry]) => { productVisible = entry.isIntersecting; update(); },
        { threshold: 0.25 }
      ).observe(product);
    }

    function update() {
      const scrolled = window.scrollY > window.innerHeight * 0.6;
      cta.classList.toggle('is-visible', scrolled && !productVisible);
    }

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /* ------------------------------------------------------ floating Ask Milo */
  /* Mirror of initFloatingCta on the other corner. It scrolls to the real chat
     card and drops the caret straight in the field, so the button lands people
     in a state where the next thing to do is obvious. No second chat panel:
     the card on the page is the only Milo, which is why this is 20 lines and
     not a duplicate widget that has to be kept in sync. */
  function initFloatingMilo() {
    const btn = $('[data-float-milo]');
    if (!btn) return;
    const section = $('#ask-milo');

    let miloVisible = false;
    if (section && 'IntersectionObserver' in window) {
      new IntersectionObserver(
        ([entry]) => { miloVisible = entry.isIntersecting; update(); },
        { threshold: 0.2 }
      ).observe(section);
    }

    function update() {
      const scrolled = window.scrollY > window.innerHeight * 0.6;
      btn.classList.toggle('is-visible', scrolled && !miloVisible);
    }

    btn.addEventListener('click', () => {
      /* Focus after the smooth scroll has settled. Focusing immediately makes
         the browser jump the field into view itself and fights the scroll. */
      const input = $('[data-milo-input]');
      if (!input) return;
      setTimeout(() => {
        input.focus({ preventScroll: true });
      }, 700);
    });

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /* ------------------------------------------------- The Furry Fix reel rail */
  /* Each card is a facade: a tinted poster that links to the Instagram profile.
     Once a reel is attached to the card, clicking swaps Instagram's own embed
     in where the poster was, so the video plays in place instead of navigating
     away. Nothing is requested from Instagram until someone actually clicks,
     which keeps 30-odd reels off the initial page load.

     data-reel accepts either a bare shortcode or a pasted permalink, because
     what you copy out of Instagram is the full URL. */
  const reelCode = (raw) => {
    const v = (raw || '').trim();
    if (!v) return '';
    const m = v.match(/instagram\.com\/(?:reel|reels|p|tv)\/([A-Za-z0-9_-]+)/);
    if (m) return m[1];
    return /^[A-Za-z0-9_-]+$/.test(v) ? v : '';
  };

  /* Instagram's embed is a whole miniature page: a 54px header with the account
     name, then the media, then likes and the caption. Samridh wants just the
     video. So the embed is loaded at a fixed width, scaled, and the wrapper
     crops everything except the video rectangle.

     The numbers are measured off the live embed at two widths, not guessed:
       - the header is a constant 54px at any width
       - the media box is always width x 1.25
     The reels are 540x960 (9:16), so inside a 4:5 media box they are
     letterboxed with black bars either side. At a fixed 480px iframe width:
       media box   480 x 600
       video       337.5 x 600, centred
       black bars  71.25px each side
     Scaling by (target width / 337.5) makes the video exactly fill the card,
     and the wrapper ends up 16/9 of the card width.

     480 is deliberate: Instagram refuses to render its embed below 326px, and
     keeping the iframe width constant means that limit is never hit, whatever
     the card is doing at that viewport. */
  const IG = { headerH: 54, boxRatio: 1.25, iframeW: 480 };

  function cropToVideo(box, frame) {
    const videoW = IG.iframeW * IG.boxRatio * 9 / 16;   /* 337.5 */
    const videoH = IG.iframeW * IG.boxRatio;            /* 600 */
    const barX = (IG.iframeW - videoW) / 2;             /* 71.25 */

    const apply = () => {
      /* clientWidth is the content box, which is what the iframe is positioned
         against. The frame is box-sizing: border-box like everything else on
         the site, so the 8px outline has to be added back when setting height
         or the video loses 16px off the bottom. */
      const target = box.clientWidth;
      if (!target) return;
      const cs = getComputedStyle(box);
      const borderY = parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth);
      const k = target / videoW;
      frame.style.width = `${IG.iframeW}px`;
      /* generous: header + media + whatever chrome sits underneath */
      frame.style.height = `${IG.headerH + videoH + 260}px`;
      frame.style.transform = `scale(${k})`;
      frame.style.transformOrigin = '0 0';
      frame.style.left = `${-barX * k}px`;
      frame.style.top = `${-IG.headerH * k}px`;
      box.style.height = `${videoH * k + borderY}px`;
    };

    apply();
    /* the rem scale system reflows on resize, so the crop has to follow */
    if ('ResizeObserver' in window) new ResizeObserver(apply).observe(box);
    else window.addEventListener('resize', apply);
  }

  function initReels() {
    const rail = $('[data-reel-rail]');
    if (!rail) return;

    /* --- click to play in place --- */
    $$('[data-reel-play]', rail).forEach((tile) => {
      const card = tile.closest('.s07b-card');
      const code = reelCode(tile.dataset.reel);
      if (!code || !card) return;          /* no reel yet: leave it as a profile link */

      tile.setAttribute('aria-label', 'Play this episode');
      tile.addEventListener('click', (e) => {
        e.preventDefault();
        if (card.classList.contains('is-playing')) return;

        /* only one reel plays at a time, otherwise the audio stacks up */
        $$('.s07b-card.is-playing', rail).forEach((other) => {
          other.classList.remove('is-playing');
          const old = $('.s07b-embed', other);
          if (old) old.remove();
        });

        const box = document.createElement('div');
        box.className = 's07b-embed';
        const frame = document.createElement('iframe');
        /* the /p/ form embeds both reels and ordinary posts; /reel/ only works
           for reels, and what gets pasted in is a mix of the two */
        frame.src = `https://www.instagram.com/p/${code}/embed/`;
        frame.title = (($('.s07b-title', card) || {}).textContent || 'Instagram reel').trim();
        frame.setAttribute('scrolling', 'no');
        frame.setAttribute('allowtransparency', 'true');
        frame.setAttribute('allow', 'autoplay; clipboard-write; encrypted-media; picture-in-picture');
        frame.setAttribute('allowfullscreen', '');
        frame.setAttribute('loading', 'eager');
        box.appendChild(frame);

        /* the cropped embed shows no Instagram branding of its own, so carry
           the tile's badge across as a real link to the post */
        const badge = $('.s07b-ig', tile);
        if (badge) {
          const link = document.createElement('a');
          link.className = 's07b-ig';
          link.href = tile.href;
          link.target = '_blank';
          link.rel = 'noopener';
          link.setAttribute('aria-label', 'Open this episode on Instagram');
          link.innerHTML = badge.innerHTML;
          box.appendChild(link);
        }

        card.insertBefore(box, tile);
        cropToVideo(box, frame);
        card.classList.add('is-playing');
      });
    });

    /* --- chevrons --- */
    const prev = $('.s07b-nav--prev');
    const next = $('.s07b-nav--next');
    const firstCard = $('.s07b-card', rail);

    /* one card plus the flex gap, measured rather than hard-coded so it stays
       right at every viewport the rem scale produces */
    const stride = () => {
      if (!firstCard) return rail.clientWidth * 0.8;
      const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
      return firstCard.getBoundingClientRect().width + gap;
    };

    const sync = () => {
      const max = rail.scrollWidth - rail.clientWidth - 1;
      /* with only a handful of reels the rail does not overflow, and a pair of
         permanently dead chevrons just looks broken */
      const wrap = rail.closest('.s07b-railwrap');
      if (wrap) wrap.classList.toggle('is-static', max <= 1);
      if (prev) prev.disabled = rail.scrollLeft <= 0;
      if (next) next.disabled = rail.scrollLeft >= max;
    };

    prev && prev.addEventListener('click', () => { rail.scrollLeft -= stride(); });
    next && next.addEventListener('click', () => { rail.scrollLeft += stride(); });
    rail.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    sync();
  }

  /* ------------------------------------------------ customer story videos */
  /* Same facade idea as the reel rail, but these are our own files rather than
     an Instagram embed, so it is a plain <video>. Nothing is fetched until the
     poster is clicked, which matters because these are multi-megabyte MP4s.

     A card with an empty data-video does nothing on click. That is the state
     it ships in until Samridh supplies the two files. */
  function initStories() {
    const players = $$('[data-video]');
    if (!players.length) return;

    players.forEach((player) => {
      const src = (player.dataset.video || '').trim();
      if (!src) {
        /* no file yet: make it obvious this is not a broken button */
        player.disabled = true;
        player.style.cursor = 'default';
        return;
      }

      player.addEventListener('click', () => {
        const frame = player.parentElement;
        if (!frame || $('video', frame)) return;

        /* one at a time, so two testimonials never talk over each other */
        $$('.s04b-frame video').forEach((v) => { v.pause(); });

        const video = document.createElement('video');
        video.src = src;
        video.controls = true;
        video.playsInline = true;
        video.preload = 'auto';
        const poster = player.dataset.poster;
        if (poster) video.poster = poster;
        frame.appendChild(video);
        player.remove();
        video.play().catch(() => { /* autoplay blocked: the controls still work */ });
      });
    });
  }

  /* ----------------------------------------------------- pre-order flow */
  /* Clicking Pre-Order used to jump straight to Shopify's cart page: a hard
     context switch, no feedback, and nothing that made the pre-order feel
     worth doing. Now the item is added over AJAX and a short confirmation
     takes its place, restating what a pre-order actually gets you.

     The anchor keeps its cart-permalink href throughout, so if the fetch
     fails, or JS never runs, the click still works exactly as before. On a
     live store that fallback matters more than the animation. */
  function initPreorder() {
    const panel = $('[data-preorder-done]');
    const links = $$('[data-preorder]');
    if (!panel || !links.length) return;

    const countOut = $('[data-preorder-count]');
    const closers = $$('[data-preorder-close]', panel);

    const close = () => {
      panel.hidden = true;
      panel.classList.remove('is-open');
    };
    closers.forEach((b) => b.addEventListener('click', close));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });

    /* A short two-note chime, synthesised rather than shipped as a file so it
       costs nothing. It only ever fires from a real click, which is what
       browsers require, and it stays silent for anyone who has asked for
       reduced motion. */
    const chime = () => {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      try {
        const ctx = new AC();
        [[660, 0], [880, 0.09]].forEach(([hz, at]) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = 'sine';
          osc.frequency.value = hz;
          gain.gain.setValueAtTime(0.0001, ctx.currentTime + at);
          gain.gain.exponentialRampToValueAtTime(0.06, ctx.currentTime + at + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + at + 0.22);
          osc.connect(gain).connect(ctx.destination);
          osc.start(ctx.currentTime + at);
          osc.stop(ctx.currentTime + at + 0.25);
        });
        setTimeout(() => ctx.close(), 600);
      } catch (e) { /* audio is a nicety, never a blocker */ }
    };

    links.forEach((link) => {
      link.addEventListener('click', async (e) => {
        /* let the plain link win if anything is unusual about the click */
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();

        if (!SHOPIFY.variantId) return;   /* unconfigured template */
        const qty = qtyState.value;
        link.classList.add('is-busy');

        try {
          /* The stepper on this page IS the quantity control, so the cart has to
             mirror it rather than accumulate. Neither endpoint does that alone:
               add.js    appends, so three clicks put three packs in the cart
                         even with the stepper still reading "Pack of 1"
               update.js only changes lines that already exist, so on an empty
                         cart it silently does nothing
             So: look first, then add or set accordingly. */
          const existing = await (await fetch('/cart.js')).json();
          const inCart = existing.items.some((i) => String(i.variant_id) === String(SHOPIFY.variantId));

          const res = inCart
            ? await fetch('/cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ updates: { [SHOPIFY.variantId]: qty } }),
              })
            : await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items: [{ id: SHOPIFY.variantId, quantity: qty }] }),
              });
          if (!res.ok) throw new Error('cart write failed');

          let total = qty;
          try {
            const cart = await (await fetch('/cart.js')).json();
            total = cart.item_count;
          } catch (err) { /* the count is decoration, not the point */ }

          if (countOut) {
            countOut.textContent = total === 1
              ? '1 pack in your cart'
              : total + ' packs in your cart';
          }
          panel.hidden = false;
          requestAnimationFrame(() => panel.classList.add('is-open'));
          panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          chime();
        } catch (err) {
          /* anything at all goes wrong: fall back to the permalink */
          window.location.href = link.href;
        } finally {
          link.classList.remove('is-busy');
        }
      });
    });
  }

  /* ------------------------------------------------------------ share links */
  /* The "Share on" row shipped as five href="#" with nothing behind them.
     Each href is built here from the live URL so it is correct on the preview
     domain, the myshopify domain and chewcheer.in without anything hardcoded.

     Instagram is deliberately not in the URL table. It has no web share
     intent: there is no address you can send a browser to that puts a link
     into an Instagram post. Pretending otherwise with a dead link is what was
     there before. Instead it uses navigator.share on devices that have it,
     which is exactly where Instagram sharing happens anyway, and falls back
     to opening the profile. */
  const SHARE = {
    url: 'https://chewcheer.in/products/prebiotic-gut-chews',
    text: 'Prebiotic gut chews for dogs, from ChewCheer.',
    profile: 'https://www.instagram.com/chewcheerr/',
  };

  function initShare() {
    const links = $$('[data-share]');
    if (!links.length) return;

    /* share the product page, but if we are already on a product page share
       the one the visitor is actually looking at */
    const url = /\/products\//.test(location.pathname)
      ? location.origin + location.pathname
      : SHARE.url;
    const u = encodeURIComponent(url);
    const t = encodeURIComponent(SHARE.text);

    const targets = {
      whatsapp: `https://wa.me/?text=${t}%20${u}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
      telegram: `https://t.me/share/url?url=${u}&text=${t}`,
      x:        `https://twitter.com/intent/tweet?url=${u}&text=${t}`,
      email:    `mailto:?subject=${encodeURIComponent('ChewCheer prebiotic gut chews')}&body=${t}%0A%0A${u}`,
    };

    links.forEach((a) => {
      const kind = a.dataset.share;

      if (kind === 'instagram') {
        a.href = SHARE.profile;
        a.addEventListener('click', (e) => {
          if (!navigator.share) return;      /* desktop: just open the profile */
          e.preventDefault();
          navigator.share({ title: 'ChewCheer', text: SHARE.text, url }).catch(() => {});
        });
        return;
      }

      const href = targets[kind];
      if (!href) return;
      a.href = href;
      /* mailto must stay in the same tab, the rest open away from the shop */
      if (kind !== 'email') { a.target = '_blank'; a.rel = 'noopener'; }
    });
  }

  /* ------------------------------------------------------------------ Milo */
  /* Figma drew Milo as a picture of a chat: a greeting, a readonly input and
     three fake suggestions. This makes it real.

     The Anthropic key is NOT here and never can be: everything in a Shopify
     theme is downloadable by anyone who opens View Source. Requests go to a
     proxy on Vercel that holds the key server-side, applies the brand's claim
     rules as a system prompt, and rate-limits per IP. */
  const MILO_ENDPOINT = 'https://chewcheer-milo.vercel.app/api/chat';

  function initMilo() {
    const thread = $('[data-milo-thread]');
    const input = $('[data-milo-input]');
    const send = $('[data-milo-send]');
    if (!thread || !input || !send) return;
    const card = thread.closest('.s09-card');

    const history = [];
    let busy = false;

    const bubble = (role, text) => {
      const el = document.createElement('div');
      el.className = 's09-msg s09-msg--' + role;
      el.textContent = text;
      thread.appendChild(el);
      thread.scrollTop = thread.scrollHeight;
      return el;
    };

    const typing = () => {
      const el = document.createElement('div');
      el.className = 's09-msg s09-msg--milo';
      el.innerHTML = '<span class="s09-typing"><span></span><span></span><span></span></span>';
      thread.appendChild(el);
      thread.scrollTop = thread.scrollHeight;
      return el;
    };

    async function ask(text) {
      const q = (text || '').trim();
      if (!q || busy) return;
      busy = true;
      input.value = '';
      input.disabled = true;
      send.disabled = true;

      if (thread.hidden) { thread.hidden = false; card && card.classList.add('is-chatting'); }
      bubble('you', q);
      history.push({ role: 'user', content: q });
      const dots = typing();

      try {
        const res = await fetch(MILO_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ messages: history }),
        });
        const data = await res.json().catch(() => ({}));
        dots.remove();

        if (!res.ok) {
          bubble('error', data.error || 'Milo could not answer that one. Try again in a moment.');
        } else {
          bubble('milo', data.reply);
          history.push({ role: 'assistant', content: data.reply });
        }
      } catch (err) {
        dots.remove();
        bubble('error', 'No connection to Milo right now. Try again shortly.');
      } finally {
        busy = false;
        input.disabled = false;
        send.disabled = false;
        input.focus();
      }
    }

    send.addEventListener('click', () => ask(input.value));
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); ask(input.value); }
    });
    $$('[data-milo-suggest]').forEach((b) => {
      b.addEventListener('click', () => ask(b.textContent));
    });
  }

  /* -------------------------------------------------------------- tab strip */
  function initTabs() {
    const tabs = $$('[data-tab]');
    if (!tabs.length) return;
    const indicator = $('[data-tab-indicator]');

    /* Figma draws a 145px rule under DESCRIPTION, whose label is 121px wide —
       so the bar sits 12px proud on each side. Keep that padding as tabs change. */
    const PAD = 12;
    const moveIndicator = (tab) => {
      if (!indicator) return;
      indicator.style.left = `${Math.max(0, tab.offsetLeft - PAD)}px`;
      indicator.style.width = `${tab.offsetWidth + PAD * 2}px`;
    };

    const select = (tab) => {
      const target = tab.dataset.tab;
      tabs.forEach((t) => {
        const on = t === tab;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', String(on));
      });
      $$('[data-tab-panel]').forEach((p) => {
        p.classList.toggle('is-active', p.dataset.tabPanel === target);
      });
      moveIndicator(tab);
    };

    tabs.forEach((t) => t.addEventListener('click', (e) => { e.preventDefault(); select(t); }));
    const active = tabs.find((t) => t.classList.contains('is-active')) || tabs[0];
    select(active);

    /* Webfonts land after first paint and change label widths; the scale system
       also reflows on resize. Re-measure in both cases. */
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => moveIndicator($('[data-tab].is-active') || active)).catch(() => {});
    }
    window.addEventListener('resize', () => moveIndicator($('[data-tab].is-active') || active));
  }

  /* -------------------------------------------------------------- accordion */
  /* FAQ. Exactly one panel open at a time; the first is open by default,
     matching the Figma design. */
  function initAccordion() {
    const items = $$('[data-accordion-item]');
    if (!items.length) return;

    const setOpen = (item, open) => {
      item.classList.toggle('is-open', open);
      const trigger = $('[data-accordion-trigger]', item);
      const panel = $('[data-accordion-panel]', item);
      if (trigger) trigger.setAttribute('aria-expanded', String(open));
      /* max-height alone drives the open/close; `hidden` would yank the panel
         out of flow and kill the transition. */
      if (panel) panel.style.maxHeight = open ? `${panel.scrollHeight}px` : '0px';
    };

    items.forEach((item, i) => {
      const trigger = $('[data-accordion-trigger]', item);
      if (!trigger) return;
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const willOpen = !item.classList.contains('is-open');
        items.forEach((other) => setOpen(other, false));
        setOpen(item, willOpen);
      });
      setOpen(item, i === 0);
    });

    window.addEventListener('resize', () => {
      items.forEach((item) => {
        if (!item.classList.contains('is-open')) return;
        const panel = $('[data-accordion-panel]', item);
        if (panel) panel.style.maxHeight = `${panel.scrollHeight}px`;
      });
    });
  }

  /* --------------------------------------------------------- reviews marquee */
  /* Duplicates the track so the loop is seamless, then animates with CSS.
     Respects prefers-reduced-motion. */
  function initMarquee() {
    const track = $('[data-marquee]');
    if (!track) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    if (!track.dataset.cloned) {
      track.innerHTML += track.innerHTML;
      track.dataset.cloned = 'true';
    }
    track.classList.add('is-animating');

    const wrap = track.parentElement;
    if (wrap) {
      wrap.addEventListener('mouseenter', () => track.classList.add('is-paused'));
      wrap.addEventListener('mouseleave', () => track.classList.remove('is-paused'));
    }
  }

  /* --------------------------------------------------------------- mobile nav */
  function initNav() {
    const toggle = $('[data-nav-toggle]');
    const nav = $('[data-nav]');
    if (!toggle || !nav) return;

    const setOpen = (open) => {
      nav.classList.toggle('is-open', open);
      toggle.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', String(open));
      document.body.style.overflow = open ? 'hidden' : '';
    };

    toggle.addEventListener('click', (e) => {
      e.preventDefault();
      setOpen(!nav.classList.contains('is-open'));
    });
    $$('a', nav).forEach((a) => a.addEventListener('click', () => setOpen(false)));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') setOpen(false); });
  }

  /* ------------------------------------------------------------ smooth scroll */
  function initSmoothScroll() {
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const id = a.getAttribute('href');
      if (!id || id === '#') return;
      const target = document.querySelector(id);
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start',
      });
    });
  }

  /* -------------------------------------------------------------------- boot */
  const boot = () => {
    initStepper();
    initGallery();
    initReels();
    initStories();
    initShare();
    initPreorder();
    initMilo();
    initTabs();
    initAccordion();
    initMarquee();
    initNav();
    initSmoothScroll();
    initFloatingCta();
    initFloatingMilo();
    syncPreorderLinks();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
